import React, { useEffect, useState } from 'react';
import { Database, ArrowRight, Bookmark } from 'lucide-react';
import { authFetch } from '../lib/api';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

/**
 * Horizontal scroll row of "frequently used" Saved Searches.
 * Each pill jumps the user directly to /export-data with that search
 * auto-loaded (the destination listens for `?load=search_id` in the URL).
 *
 * Ordering comes from the backend `/api/export-data/saved-searches/top`
 * endpoint which ranks by per-user usage count, with brand-new searches
 * surfaced via created_at as a fallback so the bar is never empty.
 *
 * The component renders nothing at all when the user has no saved
 * searches visible — the empty Dashboard remains uncluttered.
 */
export const SavedSearchPills = ({ navigate, isBarbiePink }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/export-data/saved-searches/top?limit=4`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setItems((data.saved_searches || []).slice(0, 4));
      } catch {
        // Non-fatal; the pill bar just hides itself.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || items.length === 0) return null;

  const openSearch = (search) => {
    // The /export-data page accepts a ?load=<search_id> query param and
    // auto-loads the matching saved search on mount.
    navigate(`/export-data?load=${encodeURIComponent(search.search_id)}`);
  };

  return (
    <div className="mb-2" data-testid="dashboard-saved-search-pills">
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
        <Bookmark className="w-4 h-4" />
        Saved Searches
        <span className="text-[10px] text-muted-foreground/70 font-normal normal-case tracking-normal">
          ranked by your usage
        </span>
      </h2>
      <div
        className={cn(
          'flex items-center gap-2 overflow-x-auto pb-2',
          'preview-scroll'
        )}
      >
        {items.map((s) => (
          <button
            key={s.search_id}
            onClick={() => openSearch(s)}
            className={cn(
              'group flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-card hover:bg-accent text-xs whitespace-nowrap shrink-0 transition-colors',
              isBarbiePink && 'border-pink-300 hover:bg-pink-50'
            )}
            title={`${s.name}${s.use_count ? ` · used ${s.use_count} time${s.use_count === 1 ? '' : 's'}` : ''}`}
            data-testid={`saved-search-pill-${s.search_id}`}
          >
            <Database className="w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground" />
            <span className="truncate max-w-[110px]">{s.name}</span>
            {s.has_view_state && (
              <Badge
                variant="outline"
                className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300 px-1.5 py-0"
              >
                View
              </Badge>
            )}
            {s.is_shared && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                Shared
              </Badge>
            )}
            <ArrowRight className="w-3 h-3 opacity-40 group-hover:opacity-100" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default SavedSearchPills;
